import React, { useMemo } from 'react';
import { Box, Divider, Link, Stack, Typography } from '@mui/material';
import PlaceIcon from '@mui/icons-material/Place';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import LanguageIcon from '@mui/icons-material/Language';
import { useWorkspace } from '../context/WorkspaceContext';

/**
 * @param {string} website
 * @returns {string}
 */
function normalizeHref(website) {
  if (!website) return '';
  return /^https?:\/\//i.test(website) ? website : `https://${website}`;
}

/**
 * Branded portal footer — shows company identity, address, and contact
 * details when the workspace has filled them in (Settings › Workspace ›
 * General settings). Renders nothing when there is no info to show.
 */
export default function WorkspaceFooter() {
  const { workspace } = useWorkspace();

  const profile = useMemo(() => workspace?.profile || {}, [workspace?.profile]);
  const companyName = workspace?.name || '';

  const addressLine = useMemo(() => {
    const parts = [
      profile.addressLine1,
      profile.addressLine2,
      profile.city,
      profile.stateRegion,
      profile.postalCode,
      profile.country,
    ].filter((p) => typeof p === 'string' && p.trim());
    return parts.join(', ');
  }, [profile]);

  const email = (profile.contactEmail || '').trim();
  const phone = (profile.contactPhone || '').trim();
  const website = (profile.website || '').trim();

  const hasContact = Boolean(email || phone || website);
  const hasAnything = Boolean(companyName || addressLine || hasContact);

  if (!hasAnything) return null;

  return (
    <Box
      component="footer"
      sx={{
        mt: 3,
        px: { xs: 1, sm: 2 },
        py: 2,
        color: 'text.secondary',
      }}
    >
      <Divider sx={{ mb: 2 }} />
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={{ xs: 1, md: 3 }}
        alignItems={{ xs: 'flex-start', md: 'center' }}
        justifyContent="space-between"
        useFlexGap
        flexWrap="wrap"
      >
        <Box>
          {companyName && (
            <Typography variant="body2" sx={{ fontWeight: 700, color: 'text.primary' }}>
              {companyName}
            </Typography>
          )}
          {addressLine && (
            <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mt: 0.25 }}>
              <PlaceIcon sx={{ fontSize: 16 }} />
              <Typography variant="caption">{addressLine}</Typography>
            </Stack>
          )}
        </Box>

        {hasContact && (
          <Stack
            direction="row"
            spacing={2}
            alignItems="center"
            flexWrap="wrap"
            useFlexGap
          >
            {email && (
              <Stack direction="row" spacing={0.5} alignItems="center">
                <EmailIcon sx={{ fontSize: 16 }} />
                <Link href={`mailto:${email}`} variant="caption" color="inherit" underline="hover">
                  {email}
                </Link>
              </Stack>
            )}
            {phone && (
              <Stack direction="row" spacing={0.5} alignItems="center">
                <PhoneIcon sx={{ fontSize: 16 }} />
                <Link href={`tel:${phone}`} variant="caption" color="inherit" underline="hover">
                  {phone}
                </Link>
              </Stack>
            )}
            {website && (
              <Stack direction="row" spacing={0.5} alignItems="center">
                <LanguageIcon sx={{ fontSize: 16 }} />
                <Link
                  href={normalizeHref(website)}
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="caption"
                  color="inherit"
                  underline="hover"
                >
                  {website}
                </Link>
              </Stack>
            )}
          </Stack>
        )}
      </Stack>
    </Box>
  );
}
